import type { ReactNode } from 'react';

export type RouteParams = Readonly<Record<string, string>>;

/**
 * One surface of the Studio. Every surface declares itself from its own directory;
 * the registry finds it, so no later surface has to edit a shared router file.
 */
export interface Surface {
  readonly id: string;
  /** A pattern such as `/experiments/:experimentId/explore`. */
  readonly path: string;
  readonly title: string;
  readonly render: (params: RouteParams) => ReactNode;
  /**
   * A carcass stand-in that holds a route until the slice that owns it arrives.
   * The real surface wins the path simply by registering it, and it never has to
   * touch the directory the placeholder lives in.
   */
  readonly placeholder?: boolean;
}

export function matchPath(pattern: string, pathname: string): RouteParams | null {
  const patternParts = pattern.split('/').filter((part) => part !== '');
  const pathParts = pathname.split('/').filter((part) => part !== '');
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i += 1) {
    const expected = patternParts[i] as string;
    const actual = pathParts[i] as string;
    if (expected.startsWith(':')) {
      params[expected.slice(1)] = decodeURIComponent(actual);
      continue;
    }
    if (expected !== actual) return null;
  }
  return params;
}

/** Concrete segments beat parameters, so `/apps/new` never loses to `/apps/:appId`. */
function specificity(pattern: string): number {
  return pattern.split('/').filter((part) => part !== '' && !part.startsWith(':')).length;
}

export interface MatchedRoute {
  readonly surface: Surface;
  readonly params: RouteParams;
}

export function resolveRoute(surfaces: readonly Surface[], pathname: string): MatchedRoute | null {
  const ordered = [...surfaces].sort((a, b) => specificity(b.path) - specificity(a.path));
  for (const surface of ordered) {
    const params = matchPath(surface.path, pathname);
    if (params !== null) return { surface, params };
  }
  return null;
}

/**
 * Settles who owns each route. A real surface replaces a placeholder on the same
 * path; two real surfaces on one path is a mistake nobody should discover at runtime.
 */
export function chooseSurfaces(found: readonly Surface[]): readonly Surface[] {
  const byPath = new Map<string, Surface>();
  for (const surface of found) {
    const standing = byPath.get(surface.path);
    if (standing === undefined) {
      byPath.set(surface.path, surface);
      continue;
    }
    if (standing.placeholder === true && surface.placeholder !== true) {
      byPath.set(surface.path, surface);
      continue;
    }
    if (standing.placeholder !== true && surface.placeholder === true) continue;
    throw new Error(
      `Two surfaces claim the route ${surface.path}: ${standing.id} and ${surface.id}`,
    );
  }
  return [...byPath.values()];
}
